import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Patient } from 'app/mpi/lib/patient/services/patient';
import { PatientService } from 'app/mpi/lib/patient/services/patient.service';
import { forkJoin, Observable, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';
import { SearchHttpParams, SearchResult } from '@heda/core-util';
import { EncounterService } from 'app/reception/lib/services/encounter.service';
import { Episode } from 'app/reception/lib/episode/episode';

@Component({
  template: `
    <nz-content class="page-content">
      <div class="page-content__container container">
        <nz-alert nzType="error" [nzMessage]="error" *ngIf="error"></nz-alert>
        <nz-alert nzType="success" nzMessage="Sync done" *ngIf="finished && !error"></nz-alert>
        <pre *ngIf="showUsage">
        Usage:
        ?component=patient&patientId=123
        ?component=inpatient&patientId=123
        ?component=booking&patientId=123&practitionerId=321&practitionerCode=D000 (all params optional)
        ?component=death&patientId=123&episodeId=456
      </pre
        >
        <hw-spinner [loading]="!finished"></hw-spinner>
      </div>
    </nz-content>
  `,
})
export class IesterEntryComponent implements OnInit {
  public error: string;
  public finished: boolean;
  public showUsage: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private patientService: PatientService,
    private http: HttpClient,
    private encounterService: EncounterService
  ) {}

  public ngOnInit() {
    const input = { ...this.route.snapshot.queryParams, ...this.route.snapshot.params };
    const component = input['component'] || 'patient'; //TODO: remove '|| patient'

    if (!component) {
      this.error = `'component' parameter required`;
      this.finished = true;
      this.showUsage = true;
      return;
    }

    switch (component) {
      case 'patient':
        this.openPatientEdit(input);
        break;
      case 'inpatient':
        this.openInpatientRegistration(input);
        break;
      case 'booking':
        this.openBooking(input);
        break;
      case 'death':
        this.openDeathDashboard(input);
      default:
        this.error = 'unknown component: ' + component;
        this.showUsage = true;
        this.finished = true;
    }
  }

  private openPatientEdit(input: any) {
    if (!input['patientId']) {
      this.error = "'patientId' parameter required";
      this.finished = true;
      this.showUsage = true;
      return;
    }
    forkJoin([this.syncPatient(input['patientId'])]).subscribe(
      ([hedaPatientId]) => {
        this.router.navigate(['/mpi/patients', hedaPatientId], { replaceUrl: true });
      },
      (err) => {
        this.error = 'unknown error';
        this.finished = true;
      }
    );
  }

  private openInpatientRegistration(input: any) {
    if (!input['patientId']) {
      this.error = "'patientId' parameter required";
      this.showUsage = true;
      this.finished = true;
      return;
    }
    forkJoin([this.syncPatient(input['patientId'])]).subscribe(
      ([hedaPatientId]) => {
        this.router.navigate(['/inpatients/registration', { patient: hedaPatientId }], { replaceUrl: true });
      },
      (err) => {
        this.error = 'unknown error';
        this.finished = true;
      }
    );
  }

  private openBooking(input: any) {
    forkJoin([
      this.findHedaPatient(input['patientId']),
      this.findHedaPractitioner(input['practitionerId'], input['practitionerCode']),
    ]).subscribe(
      ([hedaPatientId, hedaPractitionerId]) => {
        const p: any = {};
        if (hedaPatientId) {
          p.patient = hedaPatientId;
        }
        if (hedaPractitionerId) {
          p.practitioners = hedaPractitionerId;
        }
        this.router.navigate(['/dashboard/booking', p], { replaceUrl: true });
      },
      (err) => {
        this.error = 'unknown error';
        this.finished = true;
      }
    );
  }

  private openDeathDashboard(input: { [p: string]: any }): void {
    forkJoin([this.findHedaPatient(input['patientId']), this.findHedaEncounter(input['episodeId'])]).subscribe(
      ([patient, encounter]) => {
        this.router.navigate(['/treatment/death', { patient, encounter }], { replaceUrl: true });
      },
      (err) => {
        this.error = 'unknown error';
        this.finished = true;
      }
    );
  }

  private syncPatient(esterPatientId: number): Observable<string> {
    if (!esterPatientId) {
      return of(null);
    }
    return this.http
      .post(`${environment.estnhsApi}/hedester/patient-mapping/sync?esterId=${esterPatientId}`, {})
      .pipe(map((heda) => (heda as Patient).id));
  }

  private findHedaPatient(esterPatientId: number): Observable<string> {
    if (!esterPatientId) {
      return of(null);
    }
    return this.http
      .get(`${environment.estnhsApi}/hedester/patient-mapping/heda?esterId=${esterPatientId}`, {})
      .pipe(map((resp) => (resp as any)[0] && (resp as any)[0].id));
  }

  private findHedaPractitioner(esterPractitionerId?: number, identifiers?: string): Observable<string> {
    if (!esterPractitionerId && !identifiers) {
      return of(null);
    }
    const params: any = {};
    if (esterPractitionerId) {
      params.esterId = esterPractitionerId;
    }
    if (identifiers) {
      params.identifiers = identifiers;
    }
    return this.http
      .get(`${environment.estnhsApi}/hedester/practitioner-mapping/heda`, { params: SearchHttpParams.build(params) })
      .pipe(map((resp) => (resp as any)[0] && (resp as any)[0].id));
  }

  private findHedaEncounter(esterEpisode: string): Observable<number> {
    const params = { identifier: 'urn:ester|' + esterEpisode };
    return this.http.get(`${environment.receptionApi}/episodes`, { params: SearchHttpParams.build(params) }).pipe(
      map((resp) => {
        const result = resp as SearchResult<Episode>;
        return result.data && result.data[0] && result.data[0].id;
      }),
      flatMap((episodeId) => this.encounterService.findLastEncounter(episodeId)),
      map((encounter) => encounter.id)
    );
  }
}
